import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { analyticsAPI } from "../lib/api";
import Header from "../components/Header";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  Users, TrendingDown, TrendingUp, Activity, FileText,
  Camera, StickyNote, AlertTriangle, ArrowLeft, Trophy,
  Target, BarChart3, PieChart as PieIcon, Dumbbell, Scale
} from "lucide-react";
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from "recharts";

const COLORS = ["#00BFFF", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899"];

const StatCard = ({ icon: Icon, label, value, sub, color = "text-primary" }) => (
  <Card className="glass-card" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <CardContent className="p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0`}>
        <Icon className={`w-5 h-5 ${color}`} />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground truncate">{label}</p>
        <p className="text-lg font-extrabold text-foreground">{value}</p>
        {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
      </div>
    </CardContent>
  </Card>
);

const ChartCard = ({ title, badge, children, testId }) => (
  <Card className="glass-card" data-testid={testId}>
    <CardHeader className="pb-2">
      <div className="flex items-center justify-between">
        <CardTitle className="font-heading text-sm font-bold uppercase tracking-wider text-muted-foreground">
          {title}
        </CardTitle>
        {badge && (
          <Badge variant="outline" className="text-xs border-primary/30 text-primary">
            {badge}
          </Badge>
        )}
      </div>
    </CardHeader>
    <CardContent className="pt-0">{children}</CardContent>
  </Card>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-card/95 backdrop-blur-sm border border-border rounded-lg p-3 shadow-xl">
      <p className="text-xs font-bold text-foreground mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-xs" style={{ color: p.color }}>
          {p.name}: <span className="font-bold">{p.value}</span>
        </p>
      ))}
    </div>
  );
};

export default function AnalyticsPage() {
  const { trainer } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await analyticsAPI.getDashboard();
        setData(res.data);
      } catch {
        toast.error("Failed to load analytics");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="spinner" />
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="max-w-6xl mx-auto px-4 py-8 text-center">
          <p className="text-muted-foreground">No analytics data available.</p>
          <Button onClick={() => navigate("/dashboard")} variant="ghost" className="mt-4">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  const { summary, client_growth, goal_distribution, weight_progress, plan_activity, measurement_trends, top_performers } = data;

  return (
    <div className="min-h-screen bg-background" data-testid="analytics-page">
      <Header />
      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <Button onClick={() => navigate("/dashboard")} variant="ghost" size="sm" className="mb-2 -ml-2 text-muted-foreground hover:text-foreground">
              <ArrowLeft className="w-4 h-4 mr-1" /> Dashboard
            </Button>
            <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight uppercase text-foreground" data-testid="analytics-title">
              Analytics
            </h1>
            <p className="text-sm text-muted-foreground mt-1">Track your coaching performance</p>
          </div>
        </div>

        {/* Summary Stats Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="analytics-stats">
          <StatCard icon={Users} label="Total Clients" value={summary.total_clients} />
          <StatCard icon={Activity} label="Active" value={summary.active_clients} sub={`${summary.retention_rate}% retention`} />
          <StatCard
            icon={summary.total_weight_change <= 0 ? TrendingDown : TrendingUp}
            label="Weight Change"
            value={`${summary.total_weight_change > 0 ? '+' : ''}${summary.total_weight_change} kg`}
            color={summary.total_weight_change <= 0 ? "text-emerald-400" : "text-amber-400"}
          />
          <StatCard icon={FileText} label="Plans Created" value={summary.total_plans} />
          <StatCard
            icon={AlertTriangle}
            label="Plans Overdue"
            value={summary.plans_overdue}
            color={summary.plans_overdue > 0 ? "text-red-400" : "text-emerald-400"}
          />
        </div>

        {/* Engagement Row */}
        <div className="grid grid-cols-3 gap-3">
          <StatCard icon={Scale} label="Progress Entries" value={summary.total_progress_entries} />
          <StatCard icon={Camera} label="Photos" value={summary.total_photos} />
          <StatCard icon={StickyNote} label="Notes" value={summary.total_notes} />
        </div>

        {/* Charts Row 1: Growth + Goals */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ChartCard title="Client Growth" badge="6 Months" testId="chart-client-growth">
            {client_growth.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={client_growth}>
                  <defs>
                    <linearGradient id="gradGrowth" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#00BFFF" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#00BFFF" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis dataKey="month" tick={{ fill: '#888', fontSize: 11 }} />
                  <YAxis tick={{ fill: '#888', fontSize: 11 }} allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="total" stroke="#00BFFF" fill="url(#gradGrowth)" strokeWidth={2} name="Total" />
                  <Bar dataKey="new" fill="#10B981" radius={[4, 4, 0, 0]} name="New" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-muted-foreground text-sm text-center py-8">No data yet</p>
            )}
          </ChartCard>

          <ChartCard title="Goal Distribution" testId="chart-goal-distribution">
            {goal_distribution.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={goal_distribution} cx="50%" cy="50%" innerRadius={50} outerRadius={85} paddingAngle={4} dataKey="value">
                    {goal_distribution.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend
                    formatter={(value) => <span className="text-xs text-muted-foreground">{value}</span>}
                    iconSize={8}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-muted-foreground text-sm text-center py-8">No goals set</p>
            )}
          </ChartCard>
        </div>

        {/* Charts Row 2: Weight Progress + Plan Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ChartCard title="Weight Progress by Client" badge="kg change" testId="chart-weight-progress">
            {weight_progress.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={weight_progress} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis type="number" tick={{ fill: '#888', fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" width={80} tick={{ fill: '#888', fontSize: 10 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="change" name="Weight Change (kg)" radius={[0, 4, 4, 0]}>
                    {weight_progress.map((entry, i) => (
                      <Cell key={i} fill={entry.change <= 0 ? "#10B981" : "#F59E0B"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-muted-foreground text-sm text-center py-8">Add client weights to see progress</p>
            )}
          </ChartCard>

          <ChartCard title="Plan Activity" badge="8 Weeks" testId="chart-plan-activity">
            {plan_activity.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={plan_activity}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis dataKey="week" tick={{ fill: '#888', fontSize: 11 }} />
                  <YAxis tick={{ fill: '#888', fontSize: 11 }} allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="created" fill="#00BFFF" name="Created" radius={[4, 4, 0, 0]} stackId="a" />
                  <Bar dataKey="updated" fill="#8B5CF6" name="Updated" radius={[4, 4, 0, 0]} stackId="a" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-muted-foreground text-sm text-center py-8">No plans yet</p>
            )}
          </ChartCard>
        </div>

        {/* Charts Row 3: Measurement Trends */}
        {measurement_trends.length > 0 && (
          <ChartCard title="Body Measurement Trends" badge="Avg across clients" testId="chart-measurements">
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={measurement_trends}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis dataKey="month" tick={{ fill: '#888', fontSize: 11 }} />
                <YAxis tick={{ fill: '#888', fontSize: 11 }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend formatter={(value) => <span className="text-xs text-muted-foreground">{value}</span>} iconSize={8} />
                <Line type="monotone" dataKey="weight" stroke="#00BFFF" strokeWidth={2} dot={{ r: 4 }} name="Weight (kg)" connectNulls />
                <Line type="monotone" dataKey="bodyFat" stroke="#F59E0B" strokeWidth={2} dot={{ r: 4 }} name="Body Fat %" connectNulls />
                <Line type="monotone" dataKey="waist" stroke="#EF4444" strokeWidth={2} dot={{ r: 4 }} name="Waist (in)" connectNulls />
                <Line type="monotone" dataKey="chest" stroke="#10B981" strokeWidth={2} dot={{ r: 4 }} name="Chest (in)" connectNulls />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        )}

        {/* Top Performers */}
        {top_performers.length > 0 && (
          <ChartCard title="Top Performers" badge="Most Progress" testId="chart-top-performers">
            <div className="space-y-3">
              {top_performers.map((p, i) => (
                <div key={i} className="flex items-center gap-3 p-3 bg-white/[0.03] rounded-xl">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-extrabold text-sm ${
                    i === 0 ? 'bg-amber-500/20 text-amber-400' :
                    i === 1 ? 'bg-gray-400/20 text-gray-300' :
                    i === 2 ? 'bg-orange-500/20 text-orange-400' :
                    'bg-white/5 text-muted-foreground'
                  }`}>
                    {i === 0 ? <Trophy className="w-4 h-4" /> : `#${i + 1}`}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm text-foreground truncate">{p.name}</p>
                    <p className="text-xs text-muted-foreground">{p.starting} kg → {p.current} kg</p>
                  </div>
                  <div className="text-right">
                    <p className="font-extrabold text-sm text-emerald-400">{p.change} kg</p>
                    <p className="text-xs text-muted-foreground">{p.percent}%</p>
                  </div>
                </div>
              ))}
            </div>
          </ChartCard>
        )}

        {/* Pro Features Teaser */}
        <Card className="glass-card border-primary/20 overflow-hidden" data-testid="pro-teaser">
          <CardContent className="p-6 text-center relative">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary via-cyan-400 to-blue-500" />
            <Dumbbell className="w-8 h-8 text-primary mx-auto mb-3" />
            <h3 className="font-heading text-lg font-extrabold uppercase tracking-tight text-foreground mb-1">
              Unlock Pro Analytics
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              PDF reports, AI workout suggestions, diet plans & more
            </p>
            <Button onClick={() => navigate("/pricing")} className="btn-premium text-white font-bold px-6">
              View Pro Plans
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
